import type React from 'react'
import { useMediaQuery, useTheme, Box, Stack, Button, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, useRef } from 'react'
import TuneIcon from '@mui/icons-material/Tune'
import FilterButton from '../filter-button'
import type { MenuFilterCategory } from '../../domain/menu-category-filter'
import { useGmFiltersActionsWithTransition, useGmFiltersState } from '../../store/gmFiltersStore'

interface MobileFiltersBarProps {
  onFilterClick: () => void
}

export const MobileFiltersBar: React.FC<MobileFiltersBarProps> = ({ onFilterClick }) => {
  const theme = useTheme()
  const isMediumAndBelow = useMediaQuery(theme.breakpoints.down('lg'))
  const { t } = useTranslation()
  const { goals, allergens, dietaries, selectedCategories, itemName } = useGmFiltersState()
  const { setDietaryPreferences, setSelectedCategories, setItemName, clearFilters } =
    useGmFiltersActionsWithTransition()

  const [isSticky, setIsSticky] = useState(false)
  const filterBarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (filterBarRef.current) {
        const rect = filterBarRef.current.getBoundingClientRect()
        setIsSticky(rect.top <= 60)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (!isMediumAndBelow) {
    return null
  }

  const hasActiveFilters =
    goals.length > 0 ||
    allergens.length > 0 ||
    dietaries.length > 0 ||
    selectedCategories.length > 0 ||
    itemName.length > 0

  const handleRemoveFilter = (type: 'goals' | 'allergens' | 'dietaries', filterId: string) => {
    const currentFilters = type === 'goals' ? goals : type === 'allergens' ? allergens : dietaries
    const updatedFilters = currentFilters.filter((filter) => filter.filterInputValue !== filterId)
    setDietaryPreferences(type, updatedFilters)
  }

  const handleRemoveItemName = () => {
    setItemName('')
  }

  return (
    <Box ref={filterBarRef} sx={{ mt: 2, mb: 1 }}>
      <Box
        sx={{
          position: isSticky ? 'fixed' : 'relative',
          top: isSticky ? '60px' : 'auto',
          zIndex: 1100,
          mb: hasActiveFilters ? 1 : 0,
          transition: 'all 0.2s ease-in-out',
          py: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterButton
            onClick={onFilterClick}
            startIcon={<TuneIcon />}
            variant="white"
            sx={{
              borderRadius: 4,
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            Menu & Filters
          </FilterButton>
        </Stack>
      </Box>

      {hasActiveFilters && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          {itemName && (
            <Chip
              label={`"${itemName}"`}
              onDelete={() => {
                handleRemoveItemName()
              }}
              size="small"
              sx={{ borderRadius: 2 }}
            />
          )}

          {selectedCategories.map((category) => (
            <Chip
              key={category.id}
              label={category.name}
              onDelete={() => {
                void setSelectedCategories([
                  {
                    id: category.id,
                    name: category.name,
                    predicate: category.predicate,
                    count: 'count' in category ? (category as MenuFilterCategory).count : 0,
                    filteredCount:
                      'filteredCount' in category
                        ? (category as MenuFilterCategory).filteredCount
                        : 0,
                  },
                ])
              }}
              size="small"
              sx={{ borderRadius: 2 }}
            />
          ))}

          {goals.map((goal) => (
            <Chip
              key={goal.filterInputValue}
              label={goal.label}
              onDelete={() => {
                handleRemoveFilter('goals', goal.filterInputValue)
              }}
              size="small"
              sx={{ borderRadius: 2 }}
            />
          ))}

          {allergens.map((allergen) => (
            <Chip
              key={allergen.filterInputValue}
              label={allergen.label}
              onDelete={() => {
                handleRemoveFilter('allergens', allergen.filterInputValue)
              }}
              size="small"
              sx={{ borderRadius: 2 }}
            />
          ))}

          {dietaries.map((dietary) => (
            <Chip
              key={dietary.filterInputValue}
              label={dietary.label}
              onDelete={() => {
                handleRemoveFilter('dietaries', dietary.filterInputValue)
              }}
              size="small"
              sx={{ borderRadius: 2 }}
            />
          ))}

          <Button
            size="small"
            // startIcon={isLoading ? <CircularProgress size={16} /> : <ClearIcon />}
            // disabled={isLoading}
            onClick={() => {
              clearFilters()
            }}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              minWidth: 'auto',
              color: 'text.secondary',
              // opacity: isLoading ? 0.6 : 1,
            }}
          >
            {t('marketplace:clearAll')}
          </Button>
        </Stack>
      )}
    </Box>
  )
}
